import { useTranslations } from '@vocab/react';
import {
  Stack,
  Box,
  IconSubCategory,
  Button,
  useToast,
} from 'braid-design-system';
import { useEffect } from 'react';

import { OptionsButton } from 'src/components/atoms/Buttons';
import CommentReactionBar from 'src/components/atoms/CommentReactionBar/CommentReactionBar';
import CommentTextArea from 'src/components/atoms/CommentTextArea/CommentTextArea';
import CommentReplyCard from 'src/components/atoms/ReplyCards/CommentReplyCard';
import ThreadSkeleton from 'src/components/atoms/Skeleton/ThreadSkeleton';
import ThreadContent from 'src/components/atoms/ThreadContent/ThreadContent';
import ThreadPersonalDetails from 'src/components/atoms/ThreadPersonalDetails/ThreadPersonalDetails';
import { ContentValidationError } from 'src/core/domain/errors';
import errorTranslations from 'src/core/domain/errors/.vocab';
import { handleAuthCheck } from 'src/core/utils/authUtil';
import type { Comment } from 'src/mapper/threadMapper';
import { useAnalytics } from 'src/provider/analytics';
import { useAuthStatus } from 'src/provider/auth';
import { useLoginModal } from 'src/provider/loginModal';
import OptionDialogProvider from 'src/provider/optionDialog';
import { useProfile } from 'src/provider/profile';
import { useReplies } from 'src/provider/replies';
import { useThreadDetail } from 'src/provider/threadDetail';
import {
  updateCommentLikes,
  type updateCommentLikesParams,
} from 'src/services/threadInteractionService';

import translations from './.vocab';

import * as styles from './ThreadDetailsCommentCard.css';

interface ThreadDetailsCommentCardProps {
  commentDetails: Comment;
  threadId: string;
}

const useInitRepliesComponent = (
  threadId: string,
  id: string,
  replyCount: number,
) => {
  const { replyDispatch } = useReplies();
  useEffect(() => {
    replyDispatch({
      type: 'INIT',
      payload: { threadId, commentId: id, totalReplyCount: replyCount },
    });
  }, [replyDispatch, id, replyCount, threadId]);
};

export default ({
  threadId,
  commentDetails,
}: ThreadDetailsCommentCardProps) => {
  const showToast = useToast();
  const { t } = useTranslations(translations);
  const { t: errorTranslation } = useTranslations(errorTranslations);
  const { authStatus } = useAuthStatus();
  const {
    state: { author: replyAuthor },
  } = useProfile();
  const { trackAnalyticsInteraction } = useAnalytics();
  const { threadDetailDispatch } = useThreadDetail();
  const { openLoginDialog } = useLoginModal();

  const { id, author, creationDate, exactSocialData, liked, content } =
    commentDetails;
  const postingDate = creationDate;
  const replyCount = exactSocialData?.replyCount;
  const likeCountLabel = exactSocialData?.likeCountLabel;

  const {
    replyState,
    showMore,
    toggleShowReplies,
    toggleReply,
    handleTextChange,
    handleCreateReply,
    handleCancel,
    confirmDiscard,
    cancelDiscard,
    dismissError,
    setError,
  } = useReplies();

  const {
    replies,
    showReply,
    hasNextPage,
    isLoading,
    remainingReplyCount,
    error,
    textArea,
    showTextArea,
  } = replyState;
  useInitRepliesComponent(threadId, id, replyCount);

  const handleLikeClick = async () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    try {
      const params: updateCommentLikesParams = {
        id,
        liked: !liked,
      };
      const { countLabel, isLiked } = await updateCommentLikes(params);
      threadDetailDispatch({
        type: 'UPDATE_COMMENT_LIKE_STATUS',
        payload: { commentId: id, countLabel, isLike: isLiked },
      });
      if (countLabel) {
        trackAnalyticsInteraction('LikeCommentClick', {
          threadId,
          commentId: id,
          linkAction: isLiked ? 'like' : 'unlike',
          linkPosition: 'thread comment',
        });
      }
    } catch (err) {
      return;
    }
  };

  const handleCommentClick = () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    toggleReply();
    trackAnalyticsInteraction('ReplyClick', {
      threadId,
      commentId: id,
      linkAction: 'start reply comment',
      linkPosition: 'thread comment reply',
    });
  };

  const handleCommentSubmit = async () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    try {
      const replyId = await handleCreateReply();
      if (error === null && replyId !== '') {
        trackAnalyticsInteraction('ReplySubmit', {
          threadId,
          commentId: id,
          replyId,
          linkAction: 'reply comment posted',
          linkPosition: 'thread comment reply',
        });
        showToast({
          message: t('replyPosted'),
          tone: 'positive',
        });
      }
    } catch (err) {
      const contentError =
        err instanceof ContentValidationError
          ? err
          : new ContentValidationError();
      setError(errorTranslation(contentError.key));
    }
  };

  return (
    <Box display="flex">
      <Box
        paddingTop="small"
        paddingLeft="medium"
        paddingRight={{ mobile: 'small', desktop: 'none' }}
      >
        <IconSubCategory size="standard" tone="secondary" />
      </Box>
      <Box
        paddingLeft={{ mobile: 'none', tablet: 'small', desktop: 'small' }}
        style={{ width: '100%' }}
      >
        <Stack space="medium">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="spaceBetween"
            paddingTop="small"
            width="full"
          >
            <ThreadPersonalDetails author={author} postingDate={postingDate} />
            <OptionsButton />
          </Box>
          <Stack space="xsmall">
            <ThreadContent text={content} />
            <CommentReactionBar
              likeLabel={likeCountLabel}
              likeStatus={liked}
              onLikeClick={handleLikeClick}
              onCommentClick={handleCommentClick}
              showRepliesClick={toggleShowReplies}
              isReplyClicked={showReply}
            />
          </Stack>
          {showTextArea && (
            <Box
              padding="medium"
              className={styles.ContainerCommentTextArea}
              display={{ mobile: 'block', tablet: 'block', desktop: 'block' }}
            >
              <CommentTextArea
                id="commentTextArea"
                ariaLabel="Comment Text Area"
                value={textArea}
                author={replyAuthor}
                onChange={handleTextChange}
                handleSubmit={handleCommentSubmit}
                error={error}
                isOpen={showTextArea}
                onClose={handleCancel}
                handleDismissError={dismissError}
                discardState={replyState.discardState}
                handleDiscard={confirmDiscard}
                handleDiscardCancel={cancelDiscard}
              />
            </Box>
          )}
          {showReply &&
            replies?.length !== 0 &&
            replies?.map((reply) => (
              <OptionDialogProvider
                key={reply.id}
                value={{
                  authorId: reply.author.id,
                  contentId: reply.id,
                  interaction: 'reply',
                }}
              >
                <CommentReplyCard reply={reply} />
              </OptionDialogProvider>
            ))}
          {isLoading && (
            <ThreadSkeleton
              itemsToDisplay={
                remainingReplyCount < 10 ? remainingReplyCount : 10
              }
            />
          )}
          {showReply && hasNextPage && (
            <Box
              className={styles.ShowMoreReplies}
              padding={{ mobile: 'none', tablet: 'small', desktop: 'small' }}
              justifyContent="center"
            >
              <Button
                onClick={() => {
                  showMore();
                }}
                variant="ghost"
                tone="formAccent"
                size="small"
              >
                {t('showMoreComments')}
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
